import { HttpClient, HttpContext, HttpContextToken, HttpErrorResponse, HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http'
import { Injectable, computed, inject, signal } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject, firstValueFrom, of, throwError } from 'rxjs'
import { environment } from '../../../environments/environment'
import { NetworkStatusService } from '../../shared/services/network-status.service'
import { OutboxEntry, outboxDb } from './offline-db'
import { sessionScope } from './session-scope'

/** Marks a request as an outbox replay so the interceptor lets it through. */
export const OFFLINE_REPLAY = new HttpContextToken<boolean>(() => false)

/**
 * Endpoints never queued: identity, money, files and anything whose effect
 * can't be safely delayed or replayed. Matched against the path after baseUrl.
 */
const NEVER_QUEUE =
  /(^|\/)(auth|login|logout|register|refresh|otp|2fa|two-factor|password|sessions?|sso|oauth|billing|subscriptions?|payments?|payouts?|checkout|invoices?|wallets?|withdraw\w*|refunds?|images|media|files?|uploads?|imports?|exports?|api-keys|webhooks|domains?|mcp|notification\/(ticket|push-tokens))(\/|$|\?)/i

/** A Mongo ObjectId (24 hex) or numeric id segment — the request targets an existing record. */
const ID_SEGMENT = /\/([0-9a-f]{24}|\d+)(\/|$)/i

export type QueueDecision = 'queue' | 'reject'

/**
 * Offline edits: while the browser is offline, saves are kept in an IndexedDB
 * outbox and replayed in order as soon as the connection is back (or on the
 * next visit, if the tab was closed).
 *
 * What is queued — edits to existing records (PUT/PATCH, and POST actions on a
 * record such as `/orders/:id/notes`). What is refused with the offline
 * message — creates (POST to a collection: their pages navigate to the new
 * server id), deletes, uploads and the NEVER_QUEUE list.
 *
 * Only requests made while `navigator.onLine` is false are queued, so a
 * request can never be queued after it already reached the server.
 */
@Injectable({ providedIn: 'root' })
export class OfflineSyncService {
  private http = inject(HttpClient)
  private network = inject(NetworkStatusService)
  private translate = inject(TranslateService)

  readonly entries = signal<OutboxEntry[]>([])
  readonly syncing = signal(false)
  /** How many entries the last replay sent; null until one finishes. */
  readonly lastSynced = signal<number | null>(null)

  readonly mine = computed(() => {
    const scope = sessionScope()
    return this.entries().filter(e => e.scope === scope)
  })
  readonly pending = computed(() => this.mine().filter(e => e.status === 'pending'))
  readonly failed = computed(() => this.mine().filter(e => e.status === 'failed'))

  /** Emits after a replay that sent at least one change — pages may refetch. */
  readonly synced$ = new Subject<number>()

  private started = false

  start(): void {
    if (this.started) return
    this.started = true
    this.reload().then(() => this.flush())
    this.network.isOnline$.subscribe(online => {
      if (online) this.flush()
    })
  }

  decide(req: HttpRequest<unknown>): QueueDecision {
    if (!['POST', 'PUT', 'PATCH'].includes(req.method)) return 'reject'
    if (!req.url.startsWith(environment.baseUrl)) return 'reject'
    const body = req.body
    if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer || body instanceof URLSearchParams) return 'reject'
    const path = req.url.slice(environment.baseUrl.length)
    if (NEVER_QUEUE.test(path)) return 'reject'
    if (req.method === 'POST' && !ID_SEGMENT.test(path)) return 'reject'
    if (!sessionScope()) return 'reject'
    return 'queue'
  }

  /** Called by the interceptor for every request made while offline. */
  handleOffline(req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
    if (this.decide(req) === 'reject') {
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 0,
            statusText: 'Offline',
            url: req.urlWithParams,
            error: { message: this.translate.instant('pwa.offline.notAvailable') },
          }),
      )
    }

    const entry: OutboxEntry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      method: req.method as OutboxEntry['method'],
      url: req.urlWithParams,
      body: req.body,
      scope: sessionScope() ?? '',
      createdAt: Date.now(),
      status: 'pending',
      attempts: 0,
    }
    this.entries.update(list => [...list, entry])
    outboxDb.put(entry).catch(() => undefined)

    // Shaped like the API's usual envelope so callers' success paths run; the
    // `offline` flag lets FormPageController skip steps that need the server's
    // real response.
    return of(
      new HttpResponse({
        status: 202,
        statusText: 'Queued offline',
        url: req.urlWithParams,
        body: { success: true, offline: true, data: req.body, message: this.translate.instant('pwa.offline.queued') },
      }),
    )
  }

  /** Replay this session's pending entries in order. Stops at the first network failure. */
  async flush(): Promise<void> {
    if (this.syncing() || !this.network.isOnline()) return
    const queue = this.pending()
    if (!queue.length) return

    this.syncing.set(true)
    let sent = 0
    for (const entry of queue) {
      try {
        await firstValueFrom(this.http.request(entry.method, entry.url, { body: entry.body, context: new HttpContext().set(OFFLINE_REPLAY, true) }))
        await outboxDb.remove(entry.id).catch(() => undefined)
        this.entries.update(list => list.filter(e => e.id !== entry.id))
        sent++
      } catch (err) {
        const status = (err as HttpErrorResponse)?.status ?? 0
        if (status === 0) break // Connection dropped again — keep the rest for next time.
        const failed: OutboxEntry = {
          ...entry,
          status: 'failed',
          attempts: entry.attempts + 1,
          error: (err as HttpErrorResponse)?.error?.message || `HTTP ${status}`,
        }
        await outboxDb.put(failed).catch(() => undefined)
        this.entries.update(list => list.map(e => (e.id === entry.id ? failed : e)))
      }
    }
    this.syncing.set(false)
    if (sent) {
      this.lastSynced.set(sent)
      this.synced$.next(sent)
    }
  }

  retry(id: string): void {
    this.entries.update(list => list.map(e => (e.id === id ? { ...e, status: 'pending', error: undefined } : e)))
    const entry = this.entries().find(e => e.id === id)
    if (entry) outboxDb.put(entry).catch(() => undefined)
    this.flush()
  }

  retryAll(): void {
    for (const e of this.failed()) this.retry(e.id)
  }

  discard(id: string): void {
    this.entries.update(list => list.filter(e => e.id !== id))
    outboxDb.remove(id).catch(() => undefined)
  }

  /** Human label for an entry: the first path segment, e.g. "orders". */
  section(entry: OutboxEntry): string {
    const path = entry.url.startsWith(environment.baseUrl) ? entry.url.slice(environment.baseUrl.length) : entry.url
    return path.split(/[/?]/).filter(Boolean)[0] ?? ''
  }

  private async reload(): Promise<void> {
    try {
      this.entries.set(await outboxDb.all())
    } catch {
      this.entries.set([])
    }
  }
}
